import { useRef } from 'react';
import type { DataLayerEvent } from '../useDataLayer/types';
import useDataLayer from '../useDataLayer/useDataLayer';
import usePathname from '../usePathname/usePathname';
import { useSafeLayoutEffect } from '../useSafeLayoutEffect/useSafeLayoutEffect';
import buildPageMetaEvent, {
  type PageResourceType,
} from './buildPageMetaEvent';

type InitialPageDataLayerOptions = {
  resourceType?: PageResourceType | null;
  customerGroup?: string;
  /** Whether other pages live below this one, as reported by the CMS export. */
  hasChildren?: boolean;
  /** Replaces the default `pageView` event. */
  events?: DataLayerEvent[];
};

/**
 * Pushes the page's initial data layer events once per navigation. Passing a
 * `resourceType` additionally emits `page_meta`.
 */
export const useInitialPageDataLayer = ({
  resourceType = null,
  customerGroup,
  hasChildren,
  events = [{ event: 'pageView' }],
}: InitialPageDataLayerOptions = {}) => {
  const pathName = usePathname();
  const { pushDataLayerEvent } = useDataLayer();

  const lastPathNameRef = useRef('');

  useSafeLayoutEffect(() => {
    if (!lastPathNameRef.current || lastPathNameRef.current !== pathName) {
      // `page_meta` carries the page context, so it goes in first (ENC-11403).
      const pageMeta = buildPageMetaEvent({
        customerGroup,
        hasChildren,
        pathname: pathName,
        resourceType,
      });

      if (pageMeta) {
        pushDataLayerEvent(pageMeta);
      }

      events.forEach((event) => {
        pushDataLayerEvent(event);
      });
    }
    lastPathNameRef.current = pathName;
  }, [
    customerGroup,
    events,
    hasChildren,
    pathName,
    pushDataLayerEvent,
    resourceType,
  ]);
};
